import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Recipe } from '../../models/Recipe';
import { getAllRecipes } from '../../utils/RecipeService';
import { getCurrentUser } from '../../utils/AccountService';
import { getCurrentHousehold } from '../../utils/HouseholdService';
import { User } from '../../models/User';
import {
  EMPTY_FILTERS, MatchFilters,
  postReady, postReset, postSwipeYum, putFilters, subscribeToMatchEvents,
} from '../../utils/MatchService';
import RecipeMatchEntry from './RecipeMatchEntry';
import RecipeMatchSwipe from './RecipeMatchSwipe';
import RecipeMatchCelebrate from './RecipeMatchCelebrate';
import RecipeMatchList from './RecipeMatchList';

type Phase = 'entry' | 'swipe' | 'celebrate' | 'list';

const RecipeMatch = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('entry');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [filters, setFilters] = useState<MatchFilters>(EMPTY_FILTERS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [matches, setMatches] = useState<Recipe[]>([]);
  const [lastMatch, setLastMatch] = useState<Recipe | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [partnerName, setPartnerName] = useState('?');
  const [loading, setLoading] = useState(true);
  const [noHousehold, setNoHousehold] = useState(false);

  const [readyUsernames, setReadyUsernames] = useState<string[]>([]);
  const [waitingForPartner, setWaitingForPartner] = useState(false);

  // Stable refs so SSE callbacks always see the latest deck, recipe list and identity
  const filteredRef = useRef<Recipe[]>([]);
  // Matches are resolved against every recipe, never the filtered deck: the deck only holds what
  // this client currently has on screen, and a match that is not in it must still be recorded.
  const recipesRef = useRef<Recipe[]>([]);
  const currentUsernameRef = useRef<string | null>(null);

  useEffect(() => {
    Promise.all([getAllRecipes(), getCurrentUser(), getCurrentHousehold()])
      .then(([recipesData, user, household]) => {
        setRecipes(recipesData ?? []);
        setCurrentUser(user);
        currentUsernameRef.current = user?.username ?? null;
        if (!household) {
          setNoHousehold(true);
        } else if (user) {
          const partner = household.memberUsernames?.find((u: string) => u !== user.username);
          if (partner) setPartnerName(partner);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  recipesRef.current = recipes;

  const filteredRecipes = useMemo<Recipe[]>(() => {
    const result = recipes.filter(r => {
      if (filters.tags.length > 0 && !r.tags?.some(t => filters.tags.includes(t.toLowerCase()))) return false;
      if (filters.maxCookTime !== null && r.cookTime > filters.maxCookTime) return false;
      if (filters.difficulty !== null && r.difficulty?.toLowerCase() !== filters.difficulty) return false;
      return true;
    });
    filteredRef.current = result;
    return result;
  }, [recipes, filters]);

  // Open SSE once household is confirmed
  useEffect(() => {
    if (loading || noHousehold) return;

    const unsubscribe = subscribeToMatchEvents({
      onSessionState: (state) => {
        setReadyUsernames(state.readyUsernames);
        setFilters(state.filters ?? EMPTY_FILTERS);
        if (state.matchedRecipeIds.length > 0) {
          const restored = recipesRef.current.filter(r => r.id != null && state.matchedRecipeIds.includes(r.id));
          if (restored.length > 0) setMatches(restored);
        }
        // The browser reopens the stream by itself after a dropped connection, and any session_start
        // sent while it was down is gone for good. This snapshot is the only thing that tells such a
        // client the session is already running - without it the lobby waits forever.
        if (state.phase === 'SWIPING') {
          setWaitingForPartner(false);
          // Only lift a client that is still in the lobby: someone already swiping may have moved on
          // to the celebrate or summary screen, and must not be thrown back into the deck.
          setPhase(prev => (prev === 'entry' ? 'swipe' : prev));
        } else {
          // Back in the lobby: the session was reset while we were away, so drop the old results.
          setPhase('entry');
          setMatches([]);
          setLastMatch(null);
          setCurrentIndex(0);
          const username = currentUsernameRef.current;
          setWaitingForPartner(username != null && state.readyUsernames.includes(username));
        }
      },
      onPartnerReady: (username) => {
        setReadyUsernames(prev => prev.includes(username) ? prev : [...prev, username]);
      },
      onSessionStart: () => {
        setWaitingForPartner(false);
        setCurrentIndex(0);
        setPhase('swipe');
      },
      onMatch: (recipeId) => {
        const recipe = recipesRef.current.find(r => r.id === recipeId);
        if (!recipe) return;
        setMatches(prev => prev.some(m => m.id === recipeId) ? prev : [...prev, recipe]);
        setLastMatch(recipe);
        setPhase('celebrate');
      },
      onFiltersUpdated: (newFilters) => {
        setFilters(newFilters);
        setCurrentIndex(0);
      },
      onSessionReset: () => {
        setPhase('entry');
        setMatches([]);
        setLastMatch(null);
        setReadyUsernames([]);
        setWaitingForPartner(false);
        setFilters(EMPTY_FILTERS);
        setCurrentIndex(0);
      },
    });

    return unsubscribe;
  }, [loading, noHousehold]);

  const handleFiltersChange = useCallback((newFilters: MatchFilters) => {
    setFilters(newFilters);
    setCurrentIndex(0);
    putFilters(newFilters).catch(() => {});
  }, []);

  const handleStart = useCallback(() => {
    setWaitingForPartner(true);
    postReady().catch(() => setWaitingForPartner(false));
  }, []);

  // Way out of the lobby for a member whose partner never turned up. Resetting server-side puts
  // both members back on the entry screen instead of leaving one of them waiting on the other.
  const handleCancelWaiting = useCallback(() => {
    setWaitingForPartner(false);
    postReset().catch(() => {});
  }, []);

  // Moves the deck on by one card. The index runs past the end of the deck on the final swipe so
  // that "finished the deck" stays distinguishable from "sitting on the last card" - otherwise
  // returning from the celebrate screen puts the last recipe back on screen a second time.
  const advanceDeck = useCallback((next: number) => {
    setCurrentIndex(next);
    // A match for an earlier recipe can land while this card is still animating out. It has already
    // moved us to the celebrate screen, and pulling us back to the deck here would swallow it.
    setPhase(prev => (prev === 'celebrate'
      ? prev
      : next >= filteredRef.current.length ? 'list' : 'swipe'));
  }, []);

  const handleLike = useCallback((recipe: Recipe) => {
    if (recipe.id != null) postSwipeYum(recipe.id).catch(() => {});
    advanceDeck(currentIndex + 1);
  }, [currentIndex, advanceDeck]);

  const handlePass = useCallback(() => {
    advanceDeck(currentIndex + 1);
  }, [currentIndex, advanceDeck]);

  const handleNewSession = useCallback(() => {
    postReset().catch(() => {});
  }, []);

  // Resumes the deck where it stands - it must not advance. The swipe that produced the match has
  // already moved the deck on, and a match on a recipe liked earlier arrives while an untouched
  // card is on screen. Advancing here skipped that card, so nobody ever swiped it and it could
  // never match.
  const handleKeepSwiping = useCallback(() => {
    setPhase(currentIndex >= filteredRef.current.length ? 'list' : 'swipe');
  }, [currentIndex]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (noHousehold) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2, px: 3, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700}>{t('match_noHousehold')}</Typography>
        <Typography color="text.secondary">{t('match_noHouseholdDesc')}</Typography>
      </Box>
    );
  }

  const username = currentUser?.username ?? '?';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px)', maxWidth: 420, mx: 'auto' }}>
      {phase === 'entry' && (
        <RecipeMatchEntry
          currentUsername={username}
          partnerName={partnerName}
          readyUsernames={readyUsernames}
          waitingForPartner={waitingForPartner}
          filters={filters}
          recipeCount={filteredRecipes.length}
          onFiltersChange={handleFiltersChange}
          onStart={handleStart}
          onCancelWaiting={handleCancelWaiting}
        />
      )}
      {phase === 'swipe' && (
        <RecipeMatchSwipe
          recipes={filteredRecipes}
          currentIndex={currentIndex}
          onLike={handleLike}
          onPass={handlePass}
          onNewSession={handleNewSession}
        />
      )}
      {phase === 'celebrate' && lastMatch && (
        <RecipeMatchCelebrate
          recipe={lastMatch}
          currentUsername={username}
          partnerName={partnerName}
          onKeepSwiping={handleKeepSwiping}
          onNewSession={handleNewSession}
        />
      )}
      {phase === 'list' && (
        <RecipeMatchList
          matches={matches}
          currentUsername={username}
          partnerName={partnerName}
          onNewSession={handleNewSession}
        />
      )}
    </Box>
  );
};

export default RecipeMatch;
